/*
 * Copyright 2026 Sony Corporation
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { computeCoverage, type CoverageCamera, type CoverageOptions, type CoverageStats } from '../coverage/coverage';
import { colourOf, type CoverageMode } from '../coverage/colours';
import type { SubjectPlacement } from '../coverage/subject';
import { createCoverageSubject, type CoverageSubject } from '../scene/coverage-subject';
import type { SceneEditor } from '../scene/scene-editor';

export interface CoverageSettings extends CoverageOptions {
  mode: CoverageMode;
}

export interface UseCoverageOptions {
  enabled: boolean;
  cameras: readonly CoverageCamera[];
  settings: CoverageSettings;
  placement: SubjectPlacement;
}

export interface UseCoverageResult {
  /** Called from the canvas's onReady, once the editor exists. */
  attach: (context: { editor: SceneEditor }) => void;
  stats: CoverageStats | undefined;
}

/**
 * Long enough that a gizmo drag at 10 Hz recomputes once it pauses rather than
 * on every step, short enough to feel live.
 */
const RECOMPUTE_DELAY_MS = 120;

/**
 * The coverage subject and its heatmap (camera-simulation.md §12).
 *
 * The subject exists only while coverage is on. It is rebuilt when it moves and
 * repainted, after a short pause, whenever a camera, a lens or a threshold does.
 */
export const useCoverage = ({ enabled, cameras, settings, placement }: UseCoverageOptions): UseCoverageResult => {
  const [editor, setEditor] = useState<SceneEditor>();
  const [stats, setStats] = useState<CoverageStats>();
  const subjectRef = useRef<CoverageSubject>(null);

  const placementKey = JSON.stringify(placement);
  useEffect(() => {
    if (!editor || !enabled) {
      return;
    }
    const subject = createCoverageSubject(editor.scene, JSON.parse(placementKey) as SubjectPlacement);
    subjectRef.current = subject;
    return () => {
      subjectRef.current = null;
      subject.dispose();
    };
  }, [editor, enabled, placementKey]);

  const inputsKey = JSON.stringify({ cameras, settings });
  useEffect(() => {
    if (!editor || !enabled) {
      return;
    }
    const timer = window.setTimeout(() => {
      const subject = subjectRef.current;
      if (!subject) {
        return;
      }
      const { cameras: current, settings: options } = JSON.parse(inputsKey) as {
        cameras: CoverageCamera[];
        settings: CoverageSettings;
      };
      const result = computeCoverage(subject.samples, subject.parts, current, options);
      subject.paint((index) => colourOf(options.mode, result.best[index], result.count[index], options));
      setStats(result.stats);
    }, RECOMPUTE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [editor, enabled, inputsKey, placementKey]);

  return {
    attach: useCallback((context: { editor: SceneEditor }) => setEditor(context.editor), []),
    stats: enabled ? stats : undefined,
  };
};
